import { ArrowLeft, ChevronDown, X } from "lucide-react";
import { useMemo, useState } from "react";
import type { AddStudentPayload } from "../../../services/backend/api";

type SectionName = "A" | "B" | "C" | "D" | "E";

type AddStudentModalProps = {
  isOpen: boolean;
  schoolName: string;
  classOptions: number[];
  onClose: () => void;
  onSubmit: (
    payload: AddStudentPayload
  ) => Promise<{ ok: boolean; message: string }>;
};

const SECTIONS: SectionName[] = ["A", "B", "C", "D", "E"];

const AddStudentModal = ({
  isOpen,
  schoolName,
  classOptions,
  onClose,
  onSubmit,
}: AddStudentModalProps) => {
  const [classId, setClassId] = useState<number | "">("");
  const [section, setSection] = useState<SectionName | "">("");
  const [studentName, setStudentName] = useState("");
  const [rollNo, setRollNo] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const classes = useMemo(
    () =>
      Array.from(new Set([1, 2, 3, 4, 5, ...classOptions])).sort(
        (a, b) => a - b
      ),
    [classOptions]
  );

  if (!isOpen) return null;

  const resetForm = () => {
    setClassId("");
    setSection("");
    setStudentName("");
    setRollNo("");
    setIsSubmitting(false);
    setFeedback(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!classId || !section || !studentName.trim() || !rollNo.trim()) {
      setFeedback({ type: "error", message: "Please fill in all fields." });
      return;
    }

    setIsSubmitting(true);
    const result = await onSubmit({
      classId: Number(classId),
      section,
      studentName,
      rollNo,
    });
    setIsSubmitting(false);

    setFeedback({
      type: result.ok ? "success" : "error",
      message: result.message,
    });

    if (result.ok) {
      setTimeout(() => {
        handleClose();
      }, 500);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-[#2f1f4b]/25 p-4 backdrop-blur-[2px] md:p-6">
      <div className="absolute inset-0" onClick={handleClose} />

      <div className="relative mx-auto mt-6 w-full max-w-[720px] rounded-3xl border border-[#f0d5fb] bg-[radial-gradient(circle_at_10%_10%,#f8dbff_0%,#f3d5ff_45%,#efd2ff_100%)] p-6 shadow-2xl shadow-fuchsia-200/70">
        <button
          type="button"
          onClick={handleClose}
          className="absolute right-4 top-4 rounded-full p-1.5 text-[#a678cb] transition hover:bg-white/80 hover:text-[#6f549f]"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="mx-auto max-w-[560px] rounded-2xl border border-[#ecd7f8] bg-white/85 px-5 py-5 shadow-lg md:px-6">
          <h2 className="text-center text-2xl font-extrabold text-[#5b427f]">
            Add Student
          </h2>

          <div className="mt-4 grid grid-cols-1 gap-3">
            <div>
              <label className="mb-1 block text-xs font-bold text-[#7c62a2]">
                School
              </label>
              <div className="relative">
                <input
                  value={schoolName}
                  readOnly
                  className="w-full rounded-lg border border-[#e5cffa] bg-[#f7f0ff] px-3 py-2 text-sm text-[#7d669f]"
                />
                <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b391d1]" />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-bold text-[#7c62a2]">
                  Class
                </label>
                <div className="relative">
                  <select
                    value={classId}
                    onChange={(e) =>
                      setClassId(e.target.value ? Number(e.target.value) : "")
                    }
                    className="w-full appearance-none rounded-lg border border-[#e5cffa] bg-white px-3 py-2 pr-8 text-sm text-[#6f549f] outline-none"
                  >
                    <option value="">Select Class</option>
                    {classes.map((grade) => (
                      <option key={grade} value={grade}>
                        Grade {grade}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b391d1]" />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-bold text-[#7c62a2]">
                  Section
                </label>
                <div className="relative">
                  <select
                    value={section}
                    onChange={(e) => setSection(e.target.value as SectionName | "")}
                    className="w-full appearance-none rounded-lg border border-[#e5cffa] bg-white px-3 py-2 pr-8 text-sm text-[#6f549f] outline-none"
                  >
                    <option value="">Select Section</option>
                    {SECTIONS.map((entry) => (
                      <option key={entry} value={entry}>
                        {entry}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#b391d1]" />
                </div>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-[#7c62a2]">
                Name
              </label>
              <input
                value={studentName}
                onChange={(e) => setStudentName(e.target.value)}
                placeholder="Enter student's name"
                className="w-full rounded-lg border border-[#e5cffa] bg-white px-3 py-2 text-sm text-[#6f549f] outline-none"
              />
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold text-[#7c62a2]">
                Roll No.
              </label>
              <input
                type="number"
                min={1}
                value={rollNo}
                onChange={(e) => setRollNo(e.target.value)}
                placeholder="Enter roll number"
                className="w-full rounded-lg border border-[#e5cffa] bg-white px-3 py-2 text-sm text-[#6f549f] outline-none"
              />
            </div>

            {feedback && (
              <p
                className={`rounded-lg px-3 py-2 text-xs font-semibold ${
                  feedback.type === "success"
                    ? "bg-green-100 text-green-700"
                    : "bg-red-100 text-red-700"
                }`}
              >
                {feedback.message}
              </p>
            )}

            <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <button
                type="button"
                onClick={handleClose}
                className="inline-flex items-center justify-center gap-1 rounded-lg border border-[#e1cff5] bg-[#f8f2ff] px-4 py-2 text-sm font-semibold text-[#8f71b4]"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="rounded-lg bg-gradient-to-r from-[#31c37b] to-[#1eb074] px-4 py-2 text-sm font-bold text-white shadow-md shadow-green-200 disabled:opacity-70"
              >
                {isSubmitting ? "Adding..." : "Add Student"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddStudentModal;
